/**
 * THE BACKFILL EXCEPTIONS REGISTER — v24 Phase B, task B2.2 (spec §4-B2, AM-3).
 *
 * The CRFs the backfill could not open automatically, so Accounts can enter the
 * opening figures by hand — each row links to the CRF where that happens, which
 * is the ledger's first real use.
 *
 * WHAT THIS PAGE REFUSES TO DO, deliberately:
 *  · show a figure. The row type carries none and the page prints none. This is
 *    a worklist of WHICH CRFs, not of HOW MUCH — the money is on the CRF record
 *    behind the ledger's masking. The page test scans every rendered row for
 *    money-shaped text and fails on any.
 *  · offer a write. There is no "mark resolved": resolution is a FACT the server
 *    derives (an `opening_balance` entry exists, PB-5), and the only way to
 *    produce it is to go to the CRF and record one. The row's link is the whole
 *    call to action.
 *  · render for a seat without `payments.record`. The API also demands
 *    `amounts.view` (both keys); the nav row is gated on `payments.record`
 *    because that is the destination's identity, and AM-2's guardrail already
 *    warns when that key is held without `amounts.view`. A seat that reaches
 *    here with only one of the two gets the server's 403, rendered as the
 *    designed refusal, never an empty table.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong } from '../inquiries/format';
import { cn } from '../../lib/cn';
import { fetchPaymentExceptions, paymentKeys, registerErrorText } from './api';
import type { PaymentExceptionRow, PaymentExceptionsFilters } from './api';

const PER_PAGE = 50;

export function ExceptionsPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canWork = can(countryCode, 'payments.record');
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const includeResolved = params.get('resolved') === '1';

  const filters: PaymentExceptionsFilters = { page, perPage: PER_PAGE, includeResolved };

  const list = useQuery({
    queryKey: paymentKeys.exceptions(countryCode, filters),
    queryFn: ({ signal }) => fetchPaymentExceptions(countryCode, filters, signal),
    enabled: canWork,
  });

  const goToPage = (next: number): void => {
    const copy = new URLSearchParams(params);
    if (next <= 1) copy.delete('page');
    else copy.set('page', String(next));
    setParams(copy);
  };
  const setIncludeResolved = (on: boolean): void => {
    const copy = new URLSearchParams(params);
    if (on) copy.set('resolved', '1');
    else copy.delete('resolved');
    copy.delete('page');
    setParams(copy);
  };

  if (!canWork) {
    // D5 — a designed refusal that names who to ask and never the permission key.
    return (
      <div data-testid="payment-exceptions-refused">
        <PageHeader title="Payment exceptions" />
        <Alert tone="info" title="This register is for Accounts">
          <p>
            It lists the imported CRFs whose opening balance has to be entered by hand. Ask your
            country manager if you need it.
          </p>
        </Alert>
      </div>
    );
  }

  const rows: PaymentExceptionRow[] = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div data-testid="payment-exceptions-page">
      <PageHeader
        title="Payment exceptions"
        subtitle="Imported CRFs the backfill could not open automatically. Record each one's opening balance on the CRF; it leaves this list when you do."
        actions={
          <label className="flex items-center gap-2 text-body13 text-fg-secondary">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(event) => setIncludeResolved(event.target.checked)}
              data-testid="payment-exceptions-include-resolved"
            />
            Include resolved
          </label>
        }
      />

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the payment exceptions register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The payment exceptions register could not be loaded">
          <p>{registerErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET.
        <Alert tone="info" title={`Page ${String(page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'exception' : 'exceptions'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title={includeResolved ? 'No exceptions have been recorded' : 'Nothing is waiting'}
          description={
            includeResolved
              ? 'The backfill has not routed any CRF here yet.'
              : 'Every exception on this register has an opening balance recorded. Tick "Include resolved" to see them.'
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-line bg-surface">
            <table className="w-full text-body13" data-testid="payment-exceptions-table">
              <thead className="text-left text-fg-secondary">
                <tr className="border-b border-line">
                  <th className="px-3 py-2 font-medium">CRF</th>
                  <th className="px-3 py-2 font-medium">Party</th>
                  <th className="px-3 py-2 font-medium">Why it is here</th>
                  <th className="px-3 py-2 font-medium">Archive row</th>
                  <th className="px-3 py-2 font-medium">Listed</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn('border-b border-line last:border-b-0', row.resolved && 'text-fg-secondary')}
                    data-testid={`payment-exception-row-${row.crf_id}`}
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/countries/${countryCode}/crfs/${row.crf_id}`}
                        className="font-medium underline-offset-2 hover:underline"
                        data-testid={`payment-exception-link-${row.crf_id}`}
                      >
                        {row.crf_doc_no}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{row.party_name}</td>
                    {/* The reason IN WORDS, from the server — never an enum token. */}
                    <td className="px-3 py-2" data-testid={`payment-exception-reason-${row.crf_id}`}>
                      {row.reason_label}
                    </td>
                    <td className="px-3 py-2 tnum">{row.archive_ref}</td>
                    <td className="px-3 py-2">{formatDateLong(row.created_at)}</td>
                    <td className="px-3 py-2" data-testid={`payment-exception-status-${row.crf_id}`}>
                      {row.resolved ? 'Resolved' : 'Awaiting opening balance'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-body13 text-fg-secondary">
            <span>
              {String(total)} {total === 1 ? 'exception' : 'exceptions'}
              {includeResolved ? '' : ' awaiting an opening balance'}
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                aria-label="Previous page"
                className="rounded p-1 disabled:opacity-40"
              >
                <ChevronLeft size={16} />
              </button>
              Page {String(page)} of {String(lastPage)}
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= lastPage}
                aria-label="Next page"
                className="rounded p-1 disabled:opacity-40"
              >
                <ChevronRight size={16} />
              </button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
